'use client';

import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import { BaseCarousel } from 'components/shared/Carousel/BaseCarousel';
import { CardGrid as CarouselCardGrid } from 'components/shared/Card/CardGrid';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { cn, getRoundedCornersBox, getSectionProps } from 'utils/styles';
import { CardGridColorTheme, Colorways } from 'types/constants';
import CardGridItem from './CardGridItem';
import { CardGridProps } from './types';

const getSectionColorway = (colorTheme?: CardGridColorTheme): string =>
  colorTheme === CardGridColorTheme.PrimaryLight ? 'white-colorway' : 'gradient-light-colorway';

const getCardColorway = (colorTheme?: CardGridColorTheme): Colorways =>
  colorTheme === CardGridColorTheme.PrimaryLight ? Colorways.Inverse : Colorways.Primary;

const CardGrid = ({
  sys,
  sectionName,
  headline,
  cardGridItems,
  colorTheme,
  margins,
  roundedCorners,
}: CardGridProps) => {
  const items = cardGridItems?.items ?? [];
  const numItems = items.length;

  if (numItems === 0) {
    return null;
  }

  const cardColorway = getCardColorway(colorTheme);

  // Min 2 / max 3 cards per row:
  // 2 or 4 card grids wrap at 2 cards, otherwise wrap at 3 cards
  const desktopColumns = numItems === 2 || numItems === 4 ? 'md:grid-cols-2' : 'md:grid-cols-3';

  return (
    <section
      {...getSectionProps(
        undefined,
        margins,
        sectionName,
        cn(getSectionColorway(colorTheme), getRoundedCornersBox(roundedCorners)),
      )}
      {...liveUpdateAttrs(sys?.id, 'cardGridItems')}
      data-testid="card-grid">
      <div className="content-wrapper">
        {headline && (
          <div
            className={cn('font-display text-heading-md md:max-w-[652px] md:text-heading-xl', 'mb-6 md:mb-14')}
            {...liveUpdateAttrs(sys?.id, 'headline')}
            data-testid="card-grid-headline">
            <RichTextHeadline headline={headline} level="h2" />
          </div>
        )}

        {/* Desktop: static grid, 2 or 3 cards per row */}
        <div className={cn('hidden gap-x-6 gap-y-14 md:grid', desktopColumns)} data-testid="card-grid-desktop">
          {items.map(item => (
            <CardGridItem key={item.sys?.id} {...item} cardColorway={cardColorway} />
          ))}
        </div>
      </div>

      {/* Mobile: full-bleed single-card carousel */}
      <div className="md:hidden" data-testid="card-grid-carousel">
        <BaseCarousel
          totalItems={numItems}
          showPaginationThreshold={1}
          carouselClassName="relative flex overflow-hidden"
          carouselContentClassName="content-wrapper"
          carouselPaginationClassName="mt-6 justify-start">
          <CarouselCardGrid className="auto-cols-[100%]">
            {items.map(item => (
              <CardGridItem key={item.sys?.id} {...item} cardColorway={cardColorway} />
            ))}
          </CarouselCardGrid>
        </BaseCarousel>
      </div>
    </section>
  );
};

export default CardGrid;
